/**
 * SalesAnfragen — Lead-Pipeline Hauptseite.
 *
 * @page /sales/anfragen
 * @roles admin, manager
 * @endpoints GET /api/leads, GET /api/leads/stats, POST /api/leads, PUT /api/leads/:id, DELETE /api/leads/:id
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Search, Plus, X, ChevronDown, Users, PlusCircle, Mail, MessageSquare, Calendar, FileText, CheckCircle, XCircle, AlertCircle, Trash2, ExternalLink, Calculator, Inbox, Workflow, Pause, CheckSquare, CornerDownLeft, Circle, Activity, List, LayoutGrid } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}

const STAGES = ['alle', 'preisanfrage', 'neu', 'kontaktiert', 'termin_vereinbart', 'angebot_gesendet', 'gewonnen', 'verloren'];

// #1083 Phase 2/B — Stage-Farben Funnel-Flow: kalt→warm→heiß→won/lost mit klarem Kontrast
const STAGE_COLORS = {
  preisanfrage: 'bg-slate-50 text-slate-700 border-slate-200',
  neu: 'bg-amber-50 text-amber-700 border-amber-200',
  kontaktiert: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  termin_vereinbart: 'bg-blue-50 text-blue-700 border-blue-200',
  angebot_gesendet: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  gewonnen: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  verloren: 'bg-rose-50 text-rose-700 border-rose-200',
};

const SEGMENT_COLORS = {
  startup: 'bg-[var(--bg-inset)] text-[var(--text-secondary)]',
  midscale: 'bg-[#EBF4FD] text-[#338edc]',
  enterprise: 'bg-[#ECFEFF] text-[#0891B2]',
};

// #1083 Phase 2/C — Score-Schwellen angepasst: heiß 75+, warm 50+, kalt <50
function ScoreDot({ score }) {
  const s = score ?? 0;
  const color = s >= 75 ? 'bg-emerald-500' : s >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-[13px] font-medium text-[var(--text-primary)]">{score}</span>
    </span>
  );
}

function Badge({ children, className }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${className}`}>
      {children}
    </span>
  );
}

// #1054 — Relative Zeit (vor X Stunden/Tagen)
function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `vor ${diffD} Tag${diffD === 1 ? '' : 'en'}`;
  const diffMo = Math.round(diffD / 30);
  if (diffMo < 12) return `vor ${diffMo} Monat${diffMo === 1 ? '' : 'en'}`;
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// #1054 — Workflow-Status-Pille (5 States)
function WorkflowStatusPill({ status, name, step }) {
  if (!status || status === 'kein_workflow') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-[var(--text-tertiary)]">
        <Circle className="w-2.5 h-2.5 opacity-50" /> Kein Workflow
      </span>
    );
  }
  const map = {
    aktiv:       { Icon: Workflow,    cls: 'bg-blue-50 text-blue-700 border-blue-200',         label: 'Aktiv' },
    pausiert:    { Icon: Pause,       cls: 'bg-amber-50 text-amber-700 border-amber-300',      label: 'Pausiert' },
    beendet:     { Icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Beendet' },
    abgebrochen: { Icon: XCircle,     cls: 'bg-gray-50 text-[var(--text-tertiary)] border-[var(--border-card)]', label: 'Abgebrochen' },
  };
  const cfg = map[status] || map.abgebrochen;
  const Ic = cfg.Icon;
  const tooltip = name ? (step ? `${name} · Schritt ${step}` : name) : cfg.label;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${cfg.cls}`}
      title={tooltip}
    >
      <Ic className="w-2.5 h-2.5" /> {cfg.label}
      {name && <span className="opacity-70 truncate max-w-[120px]">· {name}</span>}
    </span>
  );
}

// #1054 — Activity-Cell (3-zeilig: Top-Priority + Workflow + Timestamp)
function ActivityCell({ lead }) {
  const needs = lead.needs_reply_count || 0;
  const unread = lead.unread_count || 0;
  const wfStatus = lead.workflow_status || 'kein_workflow';
  const lastAt = lead.last_activity_at || lead.updated_at;

  // Priorität für Top-Zeile
  let topLine = null;
  if (needs > 0) {
    topLine = (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold border border-amber-300">
        <CornerDownLeft className="w-3 h-3" />
        {needs} zu beantworten
      </span>
    );
  } else if (unread > 0) {
    topLine = (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#EBF4FD] text-[#338edc] text-[11px] font-semibold border border-[#B8D8F5]">
        <Mail className="w-3 h-3" />
        {unread} ungelesen
      </span>
    );
  } else if (wfStatus === 'pausiert') {
    topLine = <WorkflowStatusPill status={wfStatus} name={lead.workflow_name} step={lead.workflow_step} />;
  } else if (lastAt) {
    topLine = (
      <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-tertiary)]">
        <Activity className="w-3 h-3" /> Letzte Aktivität
      </span>
    );
  } else {
    topLine = (
      <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-tertiary)]">
        <Circle className="w-2.5 h-2.5 opacity-50" /> Keine Aktivität
      </span>
    );
  }

  // Workflow-Zeile (immer wenn nicht schon top)
  const showWfLine = wfStatus && wfStatus !== 'kein_workflow' && wfStatus !== 'pausiert';

  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <div className="flex items-center min-w-0">{topLine}</div>
      {showWfLine && (
        <div className="flex items-center min-w-0">
          <WorkflowStatusPill status={wfStatus} name={lead.workflow_name} step={lead.workflow_step} />
        </div>
      )}
      {wfStatus === 'kein_workflow' && (
        <div className="flex items-center min-w-0">
          <WorkflowStatusPill status="kein_workflow" />
        </div>
      )}
      {lastAt && (
        <div className="text-[11px] text-[var(--text-tertiary)] truncate">{relativeTime(lastAt)}</div>
      )}
    </div>
  );
}


// #1083 Phase 3/I — KPI mit optionalem 7-Tage-Trend (Δ vs Vor-Periode)
function KpiCard({ label, value, icon: Icon, iconColor, trend7d, trend7dPrev }) {
  const hasTrend = trend7d != null && trend7dPrev != null;
  let trendPill = null;
  if (hasTrend) {
    const delta = trend7d - trend7dPrev;
    const pct = trend7dPrev > 0 ? Math.round((delta / trend7dPrev) * 100) : (trend7d > 0 ? 100 : 0);
    const isUp = delta > 0;
    const isFlat = delta === 0;
    const cls = isFlat
      ? 'text-[var(--text-tertiary)]'
      : isUp ? 'text-emerald-600' : 'text-red-600';
    const arrow = isFlat ? '—' : isUp ? '↑' : '↓';
    trendPill = (
      <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${cls}`} title={`Letzte 7 Tage: ${trend7d} · Vor-Periode: ${trend7dPrev}`}>
        {arrow} {Math.abs(pct)}% <span className="text-[var(--text-tertiary)] font-normal">7d</span>
      </span>
    );
  }
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className={iconColor || 'text-[#338edc]'} />}
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{label}</p>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-3xl font-bold text-[var(--text-primary)]">{value}</p>
        {trendPill}
      </div>
    </div>
  );
}

// #1083 Phase 4/H — Kanban-Board mit HTML5 Drag-and-Drop
const KANBAN_STAGES = ['preisanfrage', 'neu', 'kontaktiert', 'termin_vereinbart', 'angebot_gesendet', 'gewonnen', 'verloren'];

function KanbanBoard({ leads, loading, onMove, navigate, t }) {
  const [dragOverStage, setDragOverStage] = useState(null);

  if (loading) {
    return <div className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">Loading...</div>;
  }

  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData('text/plain', String(leadId));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const onDragLeave = () => setDragOverStage(null);

  const onDrop = (e, stage) => {
    e.preventDefault();
    const leadId = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragOverStage(null);
    if (leadId) onMove(leadId, stage);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-2" style={{ minHeight: '400px' }}>
      {KANBAN_STAGES.map((stage) => {
        const colLeads = leads.filter((l) => l.stage === stage);
        const isDropTarget = dragOverStage === stage;
        return (
          <div
            key={stage}
            onDragOver={(e) => onDragOver(e, stage)}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, stage)}
            className={`flex-shrink-0 w-[260px] rounded-[12px] border bg-[var(--bg-inset)] transition-colors ${isDropTarget ? 'border-[#338edc] bg-[#EBF4FD]' : 'border-[var(--border-card)]'}`}
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--border-card)]">
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${
                  stage === 'preisanfrage' ? 'bg-slate-400' :
                  stage === 'neu' ? 'bg-amber-500' :
                  stage === 'kontaktiert' ? 'bg-cyan-500' :
                  stage === 'termin_vereinbart' ? 'bg-blue-500' :
                  stage === 'angebot_gesendet' ? 'bg-indigo-500' :
                  stage === 'gewonnen' ? 'bg-emerald-500' :
                  'bg-rose-500'
                }`} />
                <span className="text-[12px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                  {t(`sales.stages.${stage}`)}
                </span>
              </div>
              <span className="text-[11px] font-semibold text-[var(--text-tertiary)] bg-[var(--bg-card)] rounded-full px-2 py-0.5">{colLeads.length}</span>
            </div>
            <div className="p-2 space-y-2 max-h-[calc(100vh-340px)] overflow-y-auto">
              {colLeads.length === 0 ? (
                <div className="text-center py-6 text-[11px] text-[var(--text-tertiary)] italic">
                  {t('common.noData')}
                </div>
              ) : colLeads.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, lead.id)}
                  onClick={() => navigate(`/sales/leads/${lead.id}`)}
                  className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg p-2.5 cursor-grab active:cursor-grabbing hover:border-[#338edc] hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[13px] font-medium text-[var(--text-primary)] truncate flex-1">
                      {lead.firma || lead.ansprechpartner || '—'}
                    </div>
                    <ScoreDot score={lead.score} />
                  </div>
                  {lead.ansprechpartner && lead.firma && (
                    <div className="text-[11px] text-[var(--text-tertiary)] truncate mb-1.5">{lead.ansprechpartner}</div>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {lead.segment && (
                      <Badge className={SEGMENT_COLORS[lead.segment] || SEGMENT_COLORS.startup}>
                        {t(`sales.segments.${lead.segment}`) || lead.segment}
                      </Badge>
                    )}
                    {lead.zugewiesen_name && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[var(--text-tertiary)]">
                        <span className="w-3.5 h-3.5 rounded-full bg-[#338edc] text-white flex items-center justify-center text-[8px] font-semibold">
                          {(lead.zugewiesen_name || '?').trim().charAt(0).toUpperCase()}
                        </span>
                        <span className="truncate max-w-[80px]">{lead.zugewiesen_name}</span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SalesAnfragen() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('alle');
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [hasRepliesFilter, setHasRepliesFilter] = useState(''); // #1042 — '', 'unread', 'any'
  const [showNew, setShowNew] = useState(false);
  const [newLead, setNewLead] = useState({ ansprechpartner: '', firma: '', email: '', telefon: '', website: '', versandvolumen: '', nachricht: '' });
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkStage, setBulkStage] = useState('');
  const [bulkAssignId, setBulkAssignId] = useState(null);
  // #1083 Phase 4/H — View-Mode List/Kanban mit localStorage-Persistenz
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('weship_sales_anfragen_view') || 'list'; } catch { return 'list'; }
  });
  useEffect(() => {
    try { localStorage.setItem('weship_sales_anfragen_view', viewMode); } catch { /* noop */ }
  }, [viewMode]);

  // #1083 Phase 4/H — Drag-and-Drop Stage-Wechsel (optimistisch + API-Update)
  const moveLeadToStage = async (leadId, targetStage) => {
    const old = leads.find((l) => l.id === leadId);
    if (!old || old.stage === targetStage) return;
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, stage: targetStage } : l));
    try {
      await api(`/api/leads/${leadId}`, { method: 'PUT', body: { stage: targetStage } });
      load();
    } catch (e) {
      setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, stage: old.stage } : l));
      setErrorMsg(e.message);
    }
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (stage !== 'alle') params.set('stage', stage);
      if (segmentFilter) params.set('segment', segmentFilter);
      if (search) params.set('search', search);
      if (hasRepliesFilter) params.set('has_replies', hasRepliesFilter); // #1042
      const [leadsData, statsData] = await Promise.all([
        api(`/api/leads?${params}`),
        api('/api/leads/stats'),
      ]);
      setLeads(leadsData.leads || []);
      setStats(statsData);
    } catch (err) {
      console.error('Load leads error:', err);
    } finally {
      setLoading(false);
    }
  }, [stage, search, segmentFilter, hasRepliesFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/api/leads/mitarbeiter').then(setMitarbeiter).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!newLead.email && !newLead.firma) return;
    setErrorMsg('');
    try {
      await api('/api/leads', { method: 'POST', body: newLead });
      setShowNew(false);
      setNewLead({ ansprechpartner: '', firma: '', email: '', telefon: '', website: '', versandvolumen: '', nachricht: '' });
      load();
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  const handleDelete = async (id) => {
    setErrorMsg('');
    try {
      await api(`/api/leads/${id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      load();
    } catch (err) {
      setDeleteConfirm(null);
      setErrorMsg(err.message);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map((l) => l.id)));
    }
  };

  const handleBulkStage = async () => {
    if (!bulkStage) return;
    setErrorMsg('');
    try {
      await api('/api/leads/bulk-stage', { method: 'POST', body: { ids: [...selectedIds], stage: bulkStage } });
      setSelectedIds(new Set());
      setBulkAction(null);
      setBulkStage('');
      load();
    } catch (err) { setErrorMsg(err.message); }
  };

  const handleBulkAssign = async () => {
    setErrorMsg('');
    try {
      await api('/api/leads/bulk-assign', { method: 'POST', body: { ids: [...selectedIds], mitarbeiter_id: bulkAssignId } });
      setSelectedIds(new Set());
      setBulkAction(null);
      setBulkAssignId(null);
      load();
    } catch (err) { setErrorMsg(err.message); }
  };

  const handleBulkDelete = async () => {
    setErrorMsg('');
    try {
      await api('/api/leads/bulk-delete', { method: 'POST', body: { ids: [...selectedIds] } });
      setSelectedIds(new Set());
      setBulkAction(null);
      load();
    } catch (err) { setErrorMsg(err.message); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-[var(--text-primary)]">{t('sales.title')}</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{t('sales.anfragen')}</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-[13px] font-medium"
          style={{ background: 'var(--primary)' }}
        >
          <Plus className="w-4 h-4" />
          {t('sales.newLead')}
        </button>
      </div>

      {/* KPI Leiste */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        <KpiCard label={t('common.total')} value={stats.gesamt || 0} icon={Users} iconColor="text-[#338edc]" trend7d={stats.gesamt_7d} trend7dPrev={stats.gesamt_7d_prev} />
        <KpiCard label={t('sales.stages.preisanfrage')} value={stats.preisanfrage || 0} icon={Calculator} iconColor="text-[#0891B2]" iconBg="bg-[#ECFEFF]" />
        <KpiCard label={t('sales.stages.neu')} value={stats.neu || 0} icon={PlusCircle} iconColor="text-[#D97706]" iconBg="bg-[#FFF7ED]" />
        <KpiCard label={t('sales.stages.kontaktiert')} value={stats.kontaktiert || 0} icon={Mail} iconColor="text-[#2563EB]" iconBg="bg-[#EFF6FF]" />
        <KpiCard label={t('sales.stages.termin_vereinbart')} value={stats.termin_vereinbart || 0} icon={Calendar} iconColor="text-[#338edc]" iconBg="bg-[#EBF4FD]" />
        <KpiCard label={t('sales.stages.angebot_gesendet')} value={stats.angebot_gesendet || 0} icon={FileText} iconColor="text-[#6366F1]" iconBg="bg-[#EEF2FF]" />
        <KpiCard label={t('sales.stages.gewonnen')} value={stats.gewonnen || 0} icon={CheckCircle} iconColor="text-[#16A34A]" trend7d={stats.gewonnen_7d} trend7dPrev={stats.gewonnen_7d_prev} />
        <KpiCard label={t('sales.stages.verloren')} value={stats.verloren || 0} icon={XCircle} iconColor="text-[#EF4444]" iconBg="bg-[#FEF2F2]" />
      </div>

      {/* Stage Tabs + Filter */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 border-b border-[var(--border-table,#F3F4F6)]">
          {STAGES.map((s) => {
            const count = s === 'alle' ? null : (stats[s] || 0);
            return (
              <button
                key={s}
                onClick={() => setStage(s)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  stage === s
                    ? 'border-[#338edc] text-[#338edc]'
                    : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                {s === 'alle' ? t('common.all') : t(`sales.stages.${s}`)}
                {count > 0 && (
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    stage === s
                      ? 'bg-[#EBF4FD] text-[#338edc]'
                      : 'bg-[var(--bg-inset)] text-[var(--text-tertiary)]'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search') + '...'}
              className="pl-9 pr-3 py-2 rounded-2xl border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] w-[200px] focus:outline-none focus:border-[var(--primary)]"
            />
          </div>
          {/* #1083 Phase 3/G — Segment-Filter als Chips statt Select */}
          <div className="inline-flex items-center gap-1 rounded-2xl border border-[var(--border-input)] bg-[var(--bg-input)] p-0.5">
            {[
              { v: '', label: t('common.all') },
              { v: 'startup', label: t('sales.segments.startup') },
              { v: 'midscale', label: t('sales.segments.midscale') },
              { v: 'enterprise', label: t('sales.segments.enterprise') },
            ].map((c) => (
              <button
                key={c.v || 'all'}
                type="button"
                onClick={() => setSegmentFilter(c.v)}
                className={`px-2.5 py-1.5 rounded-[14px] text-[12px] font-medium transition-colors ${
                  segmentFilter === c.v
                    ? 'bg-white text-[var(--text-primary)] shadow-sm'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
                title={`${t('sales.segment')}: ${c.label}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* #1083 Phase 4/H — View-Mode Toggle Liste/Kanban */}
          <div className="inline-flex items-center gap-0.5 rounded-2xl border border-[var(--border-input)] bg-[var(--bg-input)] p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-[14px] transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
              title={t('sales.view.list') || 'Liste'}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded-[14px] transition-colors ${viewMode === 'kanban' ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
              title={t('sales.view.kanban') || 'Kanban'}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          {/* #1042 — Reply-Filter */}
          <button
            onClick={() => setHasRepliesFilter(hasRepliesFilter === 'unread' ? '' : 'unread')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-2xl text-[13px] font-medium transition-colors ${
              hasRepliesFilter === 'unread'
                ? 'bg-amber-50 border border-amber-300 text-amber-700'
                : 'bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
            }`}
            title={t('sales.filterUnreadRepliesHint') || 'Nur Leads mit ungelesenen Antworten'}
          >
            <Inbox className="w-4 h-4" />
            {t('sales.filterUnreadReplies') || 'Mit ungelesenen Antworten'}
            {stats.unread_replies_total > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800 text-[11px] font-semibold">
                {stats.unread_replies_total}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Bulk-Aktionsleiste */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 rounded-xl bg-[#EBF4FD] border border-[#B8D8F5]">
          <span className="text-[13px] font-medium text-[#338edc]">
            {t('sales.bulk.selected', { count: selectedIds.size })}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <select
                value={bulkStage}
                onChange={(e) => { setBulkStage(e.target.value); }}
                className="px-3 py-1.5 rounded-lg border border-[#B8D8F5] bg-white text-[12px] font-medium text-[#338edc] focus:outline-none"
              >
                <option value="">{t('sales.bulk.changeStage')}</option>
                {['neu', 'kontaktiert', 'termin_vereinbart', 'angebot_gesendet', 'gewonnen', 'verloren'].map((s) => (
                  <option key={s} value={s}>{t(`sales.stages.${s}`)}</option>
                ))}
              </select>
            </div>
            {bulkStage && (
              <button onClick={handleBulkStage} className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-white bg-[#338edc] hover:bg-[#2b7bc4]">
                OK
              </button>
            )}
            <div className="w-px h-5 bg-[#B8D8F5]" />
            <div className="relative">
              <select
                value={bulkAssignId || ''}
                onChange={(e) => setBulkAssignId(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-1.5 rounded-lg border border-[#B8D8F5] bg-white text-[12px] font-medium text-[#338edc] focus:outline-none"
              >
                <option value="">{t('sales.bulk.assign')}</option>
                {mitarbeiter.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            {bulkAssignId && (
              <button onClick={handleBulkAssign} className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-white bg-[#338edc] hover:bg-[#2b7bc4]">
                OK
              </button>
            )}
            <div className="w-px h-5 bg-[#B8D8F5]" />
            <button
              onClick={() => setBulkAction('delete')}
              className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-red-600 border border-red-200 bg-white hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 inline mr-1" />
              {t('sales.bulk.delete')}
            </button>
            <button onClick={() => { setSelectedIds(new Set()); setBulkStage(''); setBulkAssignId(null); }} className="p-1 hover:bg-[#D6E9FA] rounded">
              <X className="w-4 h-4 text-[#338edc]" />
            </button>
          </div>
        </div>
      )}

      {/* Tabelle */}
      {viewMode === 'list' && (
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={leads.length > 0 && selectedIds.size === leads.length}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] accent-[#338edc] cursor-pointer"
                />
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">#</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('sales.score')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('common.company')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('sales.activity') || 'Aktivität'}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('common.contact')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('sales.segment')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('sales.volume')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('common.date')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('sales.assignedTo')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{t('sales.stage')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">Loading...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">{t('common.noData')}</td></tr>
            ) : leads.map((lead) => (
              <tr
                key={lead.id}
                onClick={() => navigate(`/sales/leads/${lead.id}`)}
                className={`border-b border-[var(--border-table,#F3F4F6)] last:border-0 hover:bg-[var(--bg-card-hover)] cursor-pointer transition-colors ${selectedIds.has(lead.id) ? 'bg-[#EBF4FD]' : ''}`}
              >
                <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(lead.id)}
                    onChange={() => toggleSelect(lead.id)}
                    className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] accent-[#338edc] cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-[13px] text-[var(--text-tertiary)]">{lead.id}</td>
                <td className="px-4 py-3"><ScoreDot score={lead.score} /></td>
                <td className="px-4 py-3 text-[13px] font-medium text-[var(--text-primary)]">
                  {lead.firma || '-'}
                </td>
                {/* #1054 — Activity-Spalte: 3-zeilig (Top-Priority + Workflow + Timestamp) */}
                <td className="px-4 py-3">
                  <ActivityCell lead={lead} />
                </td>
                <td className="px-4 py-3">
                  <div className="text-[13px] text-[var(--text-primary)]">{lead.ansprechpartner || '-'}</div>
                  <div className="text-[11px] text-[var(--text-tertiary)]">{lead.email}</div>
                </td>
                <td className="px-4 py-3">
                  <Badge className={SEGMENT_COLORS[lead.segment] || SEGMENT_COLORS.startup}>
                    {t(`sales.segments.${lead.segment}`) || lead.segment}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-[13px] text-[var(--text-secondary)]">{lead.versandvolumen || '-'}</td>
                <td className="px-4 py-3 text-[13px] text-[var(--text-secondary)]">{formatDate(lead.created_at)}</td>
                {/* #1083 Phase 2/K — Owner-Naming: Mini-Avatar + Name oder „Nicht zugewiesen" */}
                {/* #1083 Phase 3/A — Auto-Indikator wenn Owner = Default-Owner (via #1054 Auto-Assign) */}
                <td className="px-4 py-3 text-[13px]">
                  {lead.zugewiesen_name ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-[#338edc] text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                        {(lead.zugewiesen_name || '?').trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="text-[var(--text-secondary)]">{lead.zugewiesen_name}</span>
                      {stats.default_owner_id && lead.zugewiesen_an === stats.default_owner_id && (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-[var(--bg-inset)] text-[var(--text-tertiary)] font-medium" title="Automatisch zugewiesen (Default-Owner)">auto</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[var(--text-tertiary)] italic">Nicht zugewiesen</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge className={STAGE_COLORS[lead.stage] || STAGE_COLORS.neu}>
                    {t(`sales.stages.${lead.stage}`) || lead.stage}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* #1083 Phase 4/H — Kanban-View mit Drag-and-Drop */}
      {viewMode === 'kanban' && (
        <KanbanBoard
          leads={leads}
          loading={loading}
          onMove={moveLeadToStage}
          navigate={navigate}
          t={t}
        />
      )}

      {/* Error Banner */}
      {errorMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[13px] shadow-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg('')} className="p-0.5 hover:bg-red-100 rounded ml-2"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-[var(--border-card)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">{t('common.confirmDelete') || 'Löschen bestätigen'}</h3>
                <p className="text-[13px] text-[var(--text-secondary)]">{t('common.deleteHint') || 'Diese Aktion kann nicht rückgängig gemacht werden.'}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] border border-[var(--border-card)] hover:bg-[var(--bg-inset)]">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={() => { handleDelete(deleteConfirm); }} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-white bg-red-600 hover:bg-red-700">
                {t('common.delete') || 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkAction === 'delete' && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setBulkAction(null)}>
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-[var(--border-card)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">{t('sales.bulk.deleteConfirm', { count: selectedIds.size })}</h3>
                <p className="text-[13px] text-[var(--text-secondary)]">{t('common.deleteHint') || 'Diese Aktion kann nicht rückgängig gemacht werden.'}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBulkAction(null)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] border border-[var(--border-card)] hover:bg-[var(--bg-inset)]">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={handleBulkDelete} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-white bg-red-600 hover:bg-red-700">
                {t('common.delete') || 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowNew(false)}>
          <div className="bg-[var(--bg-card)] rounded-[16px] border border-[var(--border-card)] shadow-dropdown w-full max-w-[520px] p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[16px] font-semibold text-[var(--text-primary)]">{t('sales.newLead')}</h2>
              <button onClick={() => setShowNew(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {['ansprechpartner', 'firma', 'email', 'telefon', 'website', 'versandvolumen'].map((f) => (
                <div key={f} className={f === 'versandvolumen' ? 'col-span-2' : ''}>
                  <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">
                    {f === 'ansprechpartner' ? t('common.contact') : f === 'firma' ? t('common.company') : f === 'versandvolumen' ? t('sales.volume') : f.charAt(0).toUpperCase() + f.slice(1)}
                  </label>
                  {f === 'versandvolumen' ? (
                    // #1083 Phase 3/J — Volume-Buckets aligned mit bestimmeSegment(): startup/midscale/enterprise
                    <select
                      value={newLead[f]}
                      onChange={(e) => setNewLead({ ...newLead, [f]: e.target.value })}
                      className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
                    >
                      <option value="">-- Bitte wählen --</option>
                      <option value="0-500">0-500 Pakete/Monat</option>
                      <option value="501-1000">501-1.000 Pakete/Monat</option>
                      <option value="1001-5000">1.001-5.000 Pakete/Monat</option>
                      <option value="5001-10000">5.001-10.000 Pakete/Monat</option>
                      <option value="10001+">10.001+ Pakete/Monat</option>
                    </select>
                  ) : (
                    <input
                      type={f === 'email' ? 'email' : 'text'}
                      value={newLead[f]}
                      onChange={(e) => setNewLead({ ...newLead, [f]: e.target.value })}
                      className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
                    />
                  )}
                </div>
              ))}
              <div className="col-span-2">
                <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{t('sales.message')}</label>
                <textarea
                  value={newLead.nachricht}
                  onChange={(e) => setNewLead({ ...newLead, nachricht: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)] resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
                {t('common.cancel')}
              </button>
              <button onClick={handleCreate} className="px-4 py-2 rounded-[8px] text-white text-[13px] font-medium" style={{ background: 'var(--primary)' }}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
